// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import * as yup from 'yup';
import { FIELD_METADATA } from '../types/policyMetadata';
import { validatePostalCode, getCountryConfig } from '../config/addressConfig';

// Maps free-text country names to supported country codes
function resolveCountryCode(countryValue: unknown): string | null {
  if (!countryValue || typeof countryValue !== 'string') return null;
  const lower = countryValue.toLowerCase().trim();
  if (lower === 'ireland' || lower === 'irl') return 'IRL';
  if (lower === 'united kingdom' || lower === 'uk' || lower === 'gbr' || lower === 'gb') return 'GBR';
  // Check if already a code
  const upper = countryValue.toUpperCase().trim();
  if (upper === 'IRL' || upper === 'GBR') return upper;
  return null;
}

// Build Yup schema dynamically from FIELD_METADATA
function buildSchema(): yup.ObjectSchema<Record<string, unknown>> {
  const shape: Record<string, yup.AnySchema> = {};

  for (const [fieldName, meta] of Object.entries(FIELD_METADATA)) {
    // Special cases first
    if (fieldName === 'customerDateOfBirth') {
      shape[fieldName] = yup.string().optional().default('')
        .test('valid-date', 'Invalid date', (val) => !val || !Number.isNaN(Date.parse(val)))
        .test('min-age-18', 'Must be at least 18 years old', (val) => {
          if (!val) return true;
          const birth = new Date(val);
          if (Number.isNaN(birth.getTime())) return true; // let valid-date handle it
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          return age >= 18;
        });
      continue;
    }

    if (fieldName === 'customerPostalCode') {
      shape[fieldName] = yup.string().optional().default('')
        .test('postal-code-format', function (val) {
          if (!val) return true;
          const countryCode = resolveCountryCode(this.parent.customerCountry);
          if (!countryCode) return true; // unsupported country, skip
          if (!validatePostalCode(countryCode, val)) {
            const config = getCountryConfig(countryCode);
            return this.createError({
              message: `Invalid ${config.postalCodeLabel} format. Expected: ${config.postalCodeFormat}`,
            });
          }
          return true;
        });
      continue;
    }

    if (fieldName === 'customerPhone') {
      shape[fieldName] = yup.string().optional().default('')
        .test('phone-format', 'Invalid phone format', (val) =>
          !val || /^\+?[0-9\s\-()]{7,20}$/.test(val)
        );
      continue;
    }

    // Generic rules by type
    switch (meta.type) {
      case 'email':
        shape[fieldName] = yup.string().optional().default('')
          .test('email-format', 'Invalid email format', (val) =>
            !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
          );
        break;

      case 'date':
        shape[fieldName] = yup.string().optional().default('')
          .test('valid-date', 'Invalid date', (val) =>
            !val || !Number.isNaN(Date.parse(val))
          );
        break;

      case 'number':
      case 'currency':
      case 'percent':
        shape[fieldName] = yup.mixed().optional()
          .test('valid-number', 'Must be a valid number (>= 0)', (val) => {
            if (val === null || val === undefined || val === '') return true;
            const num = typeof val === 'number' ? val : Number(val);
            return !Number.isNaN(num) && num >= 0;
          });
        break;

      case 'file':
        // File fields are not validated via Yup — handled by InlineFileUpload component
        shape[fieldName] = yup.mixed().optional();
        break;

      default:
        // text, textarea, select, boolean — no format validation
        shape[fieldName] = yup.mixed().optional();
        break;
    }
  }

  return yup.object(shape) as yup.ObjectSchema<Record<string, unknown>>;
}

const policyMetadataSchema = buildSchema();

/** Set of fields marked as required in FIELD_METADATA */
const REQUIRED_FIELDS = new Set(
  Object.entries(FIELD_METADATA)
    .filter(([, m]) => m.required)
    .map(([k]) => k)
);

/**
 * Validate a single field. Returns error message or empty string.
 * Required fields return an error when empty.
 * Non-required fields only validate format when non-empty.
 */
export function validateMetadataField(
  data: Record<string, unknown>,
  fieldName: string
): string {
  const value = data[fieldName];
  const isEmpty = value === null || value === undefined || value === '';

  // Required field check
  if (isEmpty && REQUIRED_FIELDS.has(fieldName)) {
    return 'This field is required';
  }

  // Skip format validation for empty non-required fields
  if (isEmpty) return '';

  // Skip yup validation for fields not defined in FIELD_METADATA
  if (!(fieldName in FIELD_METADATA)) return '';

  try {
    policyMetadataSchema.validateSyncAt(fieldName, data);
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
  }
  return '';
}

/**
 * Check if all non-empty fields in a section pass format validation.
 */
export function isSectionValid(
  data: Record<string, unknown>,
  sectionFields: string[]
): boolean {
  for (const fieldName of sectionFields) {
    if (validateMetadataField(data, fieldName)) return false;
  }
  return true;
}

// Fields that are boolean toggles — considered "filled" even when false
const BOOLEAN_FIELDS = new Set(
  Object.entries(FIELD_METADATA)
    .filter(([, m]) => m.type === 'boolean')
    .map(([k]) => k)
);

// File fields — considered "filled" even when no file is selected (optional uploads)
const FILE_FIELDS = new Set(
  Object.entries(FIELD_METADATA)
    .filter(([, m]) => m.type === 'file')
    .map(([k]) => k)
);

/**
 * Check if ALL required fields in a section are filled AND all non-empty fields pass format validation.
 * Non-required fields do not need to be filled for the section to be considered complete.
 */
function isFieldEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function isFieldValid(
  data: Record<string, unknown>,
  fieldName: string,
  value: unknown
): boolean {
  if (BOOLEAN_FIELDS.has(fieldName)) return true;
  if (FILE_FIELDS.has(fieldName)) return true;
  if (REQUIRED_FIELDS.has(fieldName) && isFieldEmpty(value)) return false;
  if (!isFieldEmpty(value)) {
    return !validateMetadataField(data, fieldName);
  }
  return true;
}

export function isSectionComplete(
  data: Record<string, unknown>,
  sectionFields: string[]
): boolean {
  return sectionFields.every((fieldName) =>
    isFieldValid(data, fieldName, data[fieldName])
  );
}
